import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Badge,
  Button,
  List,
  ListItem,
  ListIcon,
  Icon,
  Link,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaDownload, FaClock } from 'react-icons/fa';
import SessionFlowTrigger from '../components/sessionflow/SessionFlowTrigger';
import { useAnalytics } from '../hooks/useAnalytics';  

const MotionBox = motion(Box);

const TrackCard = ({ 
  badge, 
  title, 
  duration, 
  format, 
  bestFor, 
  outcomes, 
  price,
  isPrimary,
  delay,
  url,
  cta
}) => {
  const { trackEvent } = useAnalytics();  // ✅ Moved inside component

  return (
    <MotionBox
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
    >
      <VStack
        align="stretch"
        p={8}
        bg="white"
        borderRadius="2xl"
        boxShadow={isPrimary ? '0 8px 40px rgba(16, 185, 129, 0.2)' : '0 4px 20px rgba(0,0,0,0.08)'}
        border={isPrimary ? '3px solid' : '1px solid'}
        borderColor={isPrimary ? 'accent.green' : 'brand.100'}
        position="relative"
        h="full"
        _hover={{
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 50px rgba(16, 185, 129, 0.25)',
        }}
        transition="all 0.3s"
      >
        {badge && (
          <Badge
            position="absolute"
            top="-12px"
            right="20px"
            px={4}
            py={1}
            bg="accent.gold"
            color="brand.800"
            fontSize="sm"
            fontWeight="700"
            borderRadius="full"
          >
            {badge}
          </Badge>
        )}

        <Heading size="lg" color="brand.800" mb={2}>
          {title}
        </Heading>
        
        <HStack spacing={4} mb={4}>
          <HStack>
            <Icon as={FaClock} color="accent.green" />
            <Text fontWeight="600" color="brand.600">{duration}</Text>
          </HStack>
        </HStack>

        <Text color="brand.500" fontSize="sm" mb={4}>
          {format}
        </Text>

        <Box mb={4}>
          <Text fontWeight="600" color="brand.700" mb={2}>Best For:</Text>
          <Text color="brand.500" fontSize="sm">{bestFor}</Text>
        </Box>

        <Box mb={6}>
          <Text fontWeight="600" color="brand.700" mb={3}>Key Outcomes:</Text>
          <List spacing={2}>
            {outcomes.map((outcome, idx) => (
              <ListItem key={idx} fontSize="sm" color="brand.500">
                <ListIcon as={FaCheckCircle} color="accent.green" />
                {outcome}
              </ListItem>
            ))}
          </List>
        </Box>

        <Box mt="auto">
          {/* 
          <HStack justify="space-between" mb={4}>
            <Text fontSize="sm" color="brand.500">Starting from</Text>
            <Text fontSize="2xl" fontWeight="700" color="brand.800">{price}</Text>
          </HStack>
          */}

          <VStack spacing={3}>
            <Link href={cta.link}>
              <Button
                variant="outline" 
                w="full" 
                size="lg"
                leftIcon={<FaDownload />}
                onClick={() =>
                  trackEvent('view_curriculum_click', {
                    page: 'beginner_track',
                    section: 'curriculum',
                    course_length_weeks: 24
                  })
                }
              >
                View Curriculum
              </Button>
            </Link>
            {/* 
            <SessionFlowTrigger
              buttonText="Experience a Live Class"
              variant="solid"
              size="md"
              colorScheme="green"
            />
            */}
          </VStack>
        </Box>
      </VStack>
    </MotionBox>
  );
};

const TrackOverview = ({ data }) => {
  return (
    <Box py={20} bg="white">
      <Container maxW="container.xl">
        <VStack spacing={4} mb={16} textAlign="center">
          <Heading 
            size="2xl" 
            color="brand.800"
            fontWeight="900"
          >
            {data.title}
          </Heading>
          <Text fontSize="xl" color="brand.500" maxW="2xl">
            {data.subtitle}
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={12}>
          {data.tracks.map((track, index) => (
            <TrackCard key={index} {...track} />
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default TrackOverview;
